import type { Lang } from "@/i18n/translations";
import { BONUS_PUBLISHED } from "@/lib/bonusRules";

/** Pure bonus helpers (docs/features/bonus.md); the RPC layer is `@/data/bonus`. */

/** The item's name in the reader's language (the RO name when the venue gave one). */
export function bonusItemName(item: { name: string; name_ro: string | null }, lang: Lang): string {
  return lang === "ro" && item.name_ro ? item.name_ro : item.name;
}

/** Whole RON without decimals, otherwise two (15 → "15", 12.5 → "12.50"). */
export function formatRon(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

/** Seconds left until `expiresAtMs`, never negative, rounded up. */
export function secondsLeft(expiresAtMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((expiresAtMs - nowMs) / 1000));
}

/** "m:ss" for a countdown (185 → "3:05"). */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Mirrors of the `bonus_rules` values (§33b) that `my_bonus()` doesn't return.
 * Copy only: the server enforces the real values. Touchpoints of
 * docs/features/bonus.md Rule 0, so a rule change updates them too.
 */
export const BONUS_MAX_ACCURACY_M = 50;
export const BONUS_MIN_ACCOUNT_AGE_HOURS = BONUS_PUBLISHED.minAccountAgeHours;
export const BONUS_OPTOUT_HONOUR_DAYS = 30;

const LOCALE: Record<Lang, string> = { en: "en-GB", ro: "ro-RO" };

/** "17 October" — bonus dates are shown in Romanian time, like the rules' days. */
export function formatBonusDate(iso: string, lang: Lang): string {
  return new Date(iso).toLocaleDateString(LOCALE[lang], {
    day: "numeric",
    month: "long",
    timeZone: "Europe/Bucharest",
  });
}

/** "14:05" in Romanian time. */
export function formatBonusTime(iso: string | number, lang: Lang): string {
  return new Date(iso).toLocaleTimeString(LOCALE[lang], {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Bucharest",
  });
}
